import { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { APP } from './tokens';
import { PAGE_HEIGHT, PAGE_PADDING_X, PAGE_PADDING_Y, PAGE_GAP } from './pageConstants';

interface DocumentTextViewProps {
  paragraphs: string[];
  loading?: boolean;
  error?: string | null;
  /** Called when user selects text and clicks "Add Comment" */
  onSelectionComment?: (quote: string, yPercent: number) => void;
  commentMode?: boolean;
  /** Reports the total page count after pagination */
  onPageCountChange?: (count: number) => void;
  /** Called when user selects text and clicks "Ask Corner" */
  onAskCornerAboutSelection?: (snippet: { text: string; page?: number; paragraphIndex?: number }) => void;
  /** Structural anchor from edit confirmation "View" actions. */
  highlightAnchor?: string | null;
  /** When set, paragraph anchors match EditableDoc / plan_doc_edits (p{page}:block{idx}). */
  blockAnchors?: { page: number; paragraphIndex: number }[];
}

const F = APP.fontFamily;

// ─── Pagination helpers ─────────────────────────

const CONTENT_HEIGHT = PAGE_HEIGHT - PAGE_PADDING_Y * 2; // usable height per page
const LINE_HEIGHT = 22;
const LINES_PER_PAGE = Math.floor(CONTENT_HEIGHT / LINE_HEIGHT);
const CHARS_PER_LINE = 85; // approximate for page width at 14px Georgia
const PARAGRAPH_GAP_LINES = 1; // gap between paragraphs in line units

function estimateLines(text: string): number {
  const hardLines = text.split('\n');
  let total = 0;
  for (const line of hardLines) {
    total += Math.max(1, Math.ceil(line.length / CHARS_PER_LINE));
  }
  return total;
}

function paginateBlocks(paragraphs: string[]): string[][] {
  const pages: string[][] = [[]];
  let currentLines = 0;

  for (const p of paragraphs) {
    const pLines = estimateLines(p) + PARAGRAPH_GAP_LINES;
    if (currentLines + pLines > LINES_PER_PAGE && pages[pages.length - 1].length > 0) {
      pages.push([]);
      currentLines = 0;
    }
    pages[pages.length - 1].push(p);
    currentLines += pLines;
  }

  return pages;
}

// ─── Component ──────────────────────────────────

/**
 * Reader View — renders extracted document text as styled, selectable HTML
 * paginated into fixed-height Letter-sized pages.
 */
export default function DocumentTextView({
  paragraphs,
  loading,
  error,
  onSelectionComment,
  commentMode,
  onPageCountChange,
  onAskCornerAboutSelection,
  highlightAnchor,
  blockAnchors,
}: DocumentTextViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectionToolbar, setSelectionToolbar] = useState<{
    x: number;
    y: number;
    quote: string;
    yPercent: number;
    page?: number;
  } | null>(null);

  // Paginate paragraphs into pages
  const pages = useMemo(() => paginateBlocks(paragraphs), [paragraphs]);

  // Report page count
  useEffect(() => {
    onPageCountChange?.(pages.length);
  }, [pages.length, onPageCountChange]);

  useEffect(() => {
    if (!highlightAnchor) return;
    const id = `doc-anchor-${highlightAnchor.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  }, [highlightAnchor, pages.length]);

  const handleMouseUp = useCallback(() => {
    // Show toolbar if either comment mode is on or Ask Corner is available
    const hasAnyAction = (commentMode && onSelectionComment) || onAskCornerAboutSelection;
    if (!hasAnyAction) {
      setSelectionToolbar(null);
      return;
    }

    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || !sel.toString().trim()) {
      setSelectionToolbar(null);
      return;
    }

    const quote = sel.toString().trim();
    if (!quote) { setSelectionToolbar(null); return; }

    const range = sel.getRangeAt(0);
    const rangeRect = range.getBoundingClientRect();
    const container = containerRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();

    const x = rangeRect.left + rangeRect.width / 2 - containerRect.left;
    const y = rangeRect.top - containerRect.top + container.scrollTop - 8;
    const yPercent = (rangeRect.top - containerRect.top + container.scrollTop) / container.scrollHeight;

    // Try to determine which page the selection is on (1-indexed)
    let selectionPage: number | undefined;
    const startNode = range.startContainer;
    const pageEl = (startNode instanceof HTMLElement ? startNode : startNode.parentElement)?.closest('[data-page-index]');
    if (pageEl) {
      selectionPage = parseInt(pageEl.getAttribute('data-page-index') ?? '0', 10) + 1;
    }

    setSelectionToolbar({ x, y, quote, yPercent: Math.max(0, Math.min(1, yPercent)), page: selectionPage });
  }, [commentMode, onSelectionComment, onAskCornerAboutSelection]);

  // Dismiss toolbar when clicking outside
  useEffect(() => {
    if (!selectionToolbar) return;
    const handleClick = (e: MouseEvent) => {
      const toolbar = document.getElementById('selection-toolbar');
      if (toolbar && toolbar.contains(e.target as Node)) return;
      setSelectionToolbar(null);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [selectionToolbar]);

  if (loading) {
    return (
      <div style={singlePageStyle}>
        <div style={{ padding: `${PAGE_PADDING_Y}px ${PAGE_PADDING_X}px`, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {[95, 80, 88, 72, 90, 65, 85, 78, 92, 75, 88, 68].map((w, i) => (
            <div
              key={i}
              style={{
                height: 14,
                borderRadius: 4,
                backgroundColor: '#E8E0D8',
                opacity: 0.5,
                width: `${w}%`,
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...singlePageStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: 13, color: '#9A8E82', fontFamily: F }}>{error}</span>
      </div>
    );
  }

  if (paragraphs.length === 0) {
    return (
      <div style={{ ...singlePageStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: 13, color: '#9A8E82', fontFamily: F }}>
          No text could be extracted from this document.
        </span>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      onMouseUp={handleMouseUp}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: PAGE_GAP,
        cursor: commentMode ? 'text' : undefined,
        userSelect: 'text',
      }}
    >
      {pages.map((pageBlocks, pageIndex) => {
        const previousCount = pages.slice(0, pageIndex).reduce((sum, page) => sum + page.length, 0);
        return (
        <div
          key={pageIndex}
          data-page-index={pageIndex}
          style={{
            width: '100%',
            height: PAGE_HEIGHT,
            backgroundColor: APP.bgDocSheet,
            boxShadow: APP.docSheetShadow,
            borderRadius: 4,
            padding: `${PAGE_PADDING_Y}px ${PAGE_PADDING_X}px`,
            overflow: 'hidden',
            flexShrink: 0,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          {pageBlocks.map((p, i) => {
            const paragraphIndex = previousCount + i;
            const meta = blockAnchors?.[paragraphIndex];
            const anchor = meta
              ? `p${meta.page}:block${meta.paragraphIndex}`
              : `p${pageIndex + 1}:block${paragraphIndex}`;
            const isHighlighted = highlightAnchor === anchor;
            return (
            <p
              key={i}
              id={`doc-anchor-${anchor.replace(/[^a-zA-Z0-9_-]/g, '-')}`}
              style={{
                fontFamily: '"Georgia", serif',
                fontSize: 14,
                lineHeight: `${LINE_HEIGHT}px`,
                color: '#2E2318',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                backgroundColor: isHighlighted ? '#FFF1D8' : undefined,
                outline: isHighlighted ? '2px solid #D97645' : undefined,
                borderRadius: isHighlighted ? 3 : undefined,
                transition: 'background-color 180ms ease, outline-color 180ms ease',
              }}
            >
              {p}
            </p>
          );})}
        </div>
      );})}

      {/* Selection toolbar — floating action buttons */}
      {selectionToolbar && (
        <div
          id="selection-toolbar"
          style={{
            position: 'absolute',
            left: selectionToolbar.x,
            top: selectionToolbar.y,
            transform: 'translate(-50%, -100%)',
            zIndex: 60,
            display: 'flex',
            gap: 4,
          }}
        >
          {/* Ask Corner button — always shown when handler exists */}
          {onAskCornerAboutSelection && (
            <button
              type="button"
              onClick={() => {
                onAskCornerAboutSelection({
                  text: selectionToolbar.quote,
                  page: selectionToolbar.page,
                });
                setSelectionToolbar(null);
                window.getSelection()?.removeAllRanges();
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                padding: '6px 12px',
                borderRadius: 8,
                border: 'none',
                backgroundColor: '#D97645',
                color: '#FDFCFB',
                fontFamily: F,
                fontSize: 12,
                fontWeight: 500,
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                whiteSpace: 'nowrap',
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z" fill="#FDFCFB" />
              </svg>
              Ask Corner
            </button>
          )}
          {/* Add Comment button — shown in comment mode */}
          {commentMode && onSelectionComment && (
            <button
              type="button"
              onClick={() => {
                onSelectionComment(selectionToolbar.quote, selectionToolbar.yPercent);
                setSelectionToolbar(null);
                window.getSelection()?.removeAllRanges();
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                padding: '6px 12px',
                borderRadius: 8,
                border: 'none',
                backgroundColor: '#2E2318',
                color: '#FDFCFB',
                fontFamily: F,
                fontSize: 12,
                fontWeight: 500,
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                whiteSpace: 'nowrap',
              }}
            >
              <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                <path
                  d="M2 2.5h10A1.5 1.5 0 0113.5 4v5a1.5 1.5 0 01-1.5 1.5H5L2 13V4A1.5 1.5 0 013.5 2.5H2Z"
                  stroke="#FDFCFB"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Add Comment
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const singlePageStyle: React.CSSProperties = {
  width: '100%',
  height: PAGE_HEIGHT,
  borderRadius: 4,
  backgroundColor: APP.bgDocSheet,
  boxShadow: APP.docSheetShadow,
  boxSizing: 'border-box',
};
